import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DownloadSimple, UploadSimple, Warning, FileXls } from "@phosphor-icons/react";
import { UpgradeBanner } from "@/components/PlanGating";
import { usePlanFlags } from "@/contexts/PlanFlagsContext";

export default function DataTab() {
  const { user } = useAuth();
  const { flags } = usePlanFlags();
  const isOwner = (user?.roles || []).includes("owner");
  const canImport = flags?.has_excel_import !== false;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [replaceOnImport, setReplaceOnImport] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  // iter-67k (Area 4) — JSON archive export/import state.
  const archiveFileRef = useRef(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveResult, setArchiveResult] = useState(null);

  const onArchiveExport = async () => {
    setArchiveBusy(true);
    try {
      const res = await api.post("/data/archive/export", null, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      link.download = m ? m[1] : `therapyflow-archive-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Archive downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchiveBusy(false); }
  };

  const onArchiveImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Restore this archive? Records with the same ID will be replaced.")) {
      e.target.value = "";
      return;
    }
    setArchiveBusy(true);
    setArchiveResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/data/archive/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setArchiveResult(data.summary || {});
      toast.success("Archive restored");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setArchiveBusy(false); e.target.value = ""; }
  };

  if (!isOwner) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          <Warning size={28} className="mx-auto mb-2 text-amber-600" weight="duotone" />
          Only the owner can access data lifecycle tools (export, import, factory reset).
        </CardContent>
      </Card>
    );
  }

  const download = async (url, fallbackName) => {
    setBusy(true);
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      // Extract filename from Content-Disposition if present
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      link.download = m ? m[1] : fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("Download started");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    setBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(`/admin/import?replace=${replaceOnImport}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data.result);
      toast.success("Import completed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onFactoryReset = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/factory-reset");
      toast.success(`Reset complete — ${Object.values(res.data.deleted || {}).reduce((a,b)=>a+b,0)} records removed`);
      setShowReset(false); setResetConfirm("");
      // Force a page reload so cached data disappears
      setTimeout(() => window.location.reload(), 600);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* Export */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <DownloadSimple size={18} weight="duotone" /> Export data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Download an Excel workbook containing every record in the system — one sheet per entity (Branches, TherapyTypes, Users, Students, Sessions, Invoices, Payments, Attendance, Inventory, Certifications, Grants). Use the <span className="font-medium">Template</span> option to grab an empty version of the same workbook for a fresh setup.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => download("/admin/export/template", "therapyflow-template.xlsx")} disabled={busy} className="gap-1.5" data-testid="download-template">
              <FileXls size={14} weight="duotone" /> Download empty template
            </Button>
            <Button onClick={() => download("/admin/export", "therapyflow-export.xlsx")} disabled={busy} className="gap-1.5" data-testid="download-export">
              <DownloadSimple size={14} weight="bold" /> Download full data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* iter-67k (Area 4) — Full workspace JSON archive. Sits
          alongside the existing Excel export/import so we never
          regress that flow. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <DownloadSimple size={18} weight="duotone" /> Full workspace archive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Download a ZIP containing every record across attendance, sessions, billing, students, employees, messages, settings, subscriptions and more — for backup or migrating to another workspace. Import restores everything, upserting by record ID so no duplicates are created.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onArchiveExport} disabled={archiveBusy} className="gap-1.5" data-testid="archive-export-btn">
              <DownloadSimple size={14} weight="bold" /> {archiveBusy ? "Preparing…" : "Download archive (.zip)"}
            </Button>
            <input
              ref={archiveFileRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={onArchiveImport}
              data-testid="archive-file-input"
            />
            <Button
              variant="outline"
              onClick={() => archiveFileRef.current?.click()}
              disabled={archiveBusy}
              className="gap-1.5"
              data-testid="archive-import-btn"
            >
              <UploadSimple size={14} weight="bold" /> {archiveBusy ? "Restoring…" : "Restore from archive"}
            </Button>
          </div>
          {archiveResult && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="archive-import-result">
              <div className="font-semibold text-emerald-900 mb-1">Archive restored</div>
              <ul className="space-y-0.5 text-emerald-900 max-h-40 overflow-y-auto">
                {Object.entries(archiveResult).map(([k, v]) => (
                  <li key={k}>
                    · <span className="font-medium">{k}</span>: {v.created} created, {v.updated} updated, {v.skipped} skipped
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <UploadSimple size={18} weight="duotone" /> Import data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <UpgradeBanner flagKey="has_excel_import" />
          <p className="text-sm text-muted-foreground">
            Upload a workbook in the same format as the export/template. Rows with matching <span className="font-mono text-xs">code</span> / <span className="font-mono text-xs">email</span> / <span className="font-mono text-xs">id</span> are skipped so you can safely re-import. New users need a <span className="font-mono text-xs">password</span> value on their row.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={replaceOnImport} onCheckedChange={(v) => setReplaceOnImport(!!v)} data-testid="replace-on-import" />
            <span>Replace all existing data first (factory-reset before import)</span>
          </label>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={onUpload}
              data-testid="import-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={busy || !canImport}
              className="gap-1.5"
              data-testid="upload-btn"
            >
              <UploadSimple size={14} weight="bold" /> {busy ? "Uploading…" : "Choose .xlsx file"}
            </Button>
          </div>
          {importResult && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="import-result">
              <div className="font-semibold text-emerald-900 mb-1">Import summary</div>
              <ul className="space-y-0.5 text-emerald-900">
                {Object.entries(importResult).map(([k, v]) => (
                  <li key={k}>
                    · <span className="font-medium capitalize">{k.replace("_", " ")}</span>: {v.created} created, {v.skipped} skipped{typeof v.missing_password === "number" && v.missing_password > 0 ? `, ${v.missing_password} missing password` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factory reset */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2 text-red-900">
            <Warning size={18} weight="fill" className="text-red-600" /> Factory reset
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Wipe every business record in the system — branches, users (except owners), students, sessions, invoices, payments, attendance, inventory, certifications, grants. This action is <span className="font-semibold text-red-800">irreversible</span>. Consider downloading a full export first.
          </p>
          <Button
            variant="outline"
            className="border-red-300 text-red-800 hover:bg-red-50 gap-1.5"
            onClick={() => setShowReset(true)}
            disabled={busy}
            data-testid="reset-btn"
          >
            <Warning size={14} weight="bold" /> Factory reset the system
          </Button>
        </CardContent>
      </Card>

      <Dialog open={showReset} onOpenChange={(o) => { setShowReset(o); if (!o) setResetConfirm(""); }}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight flex items-center gap-2 text-red-900"><Warning size={18} weight="fill" className="text-red-600" /> Confirm factory reset</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900">
              This will permanently delete every branch, user (except owners), student, session, invoice, payment, attendance record, inventory item, certification, and grant. This action cannot be undone.
            </div>
            <div className="space-y-1.5">
              <Label>Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
              <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReset(false)}>Cancel</Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-800 hover:bg-red-50"
              onClick={onFactoryReset}
              disabled={busy || resetConfirm !== "RESET"}
              data-testid="reset-confirm-btn"
            >
              {busy ? "Wiping…" : "Factory reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
